// A request that never gets a response (a stalled mobile connection, a dead
// keep-alive socket) makes the browser wait a minute or more before giving
// up — and supabase-js queues everything behind its session check, so one
// stuck request can make the whole app look frozen at startup. Ordinary API
// calls get a deadline instead. Retrying a read that timed out is left to supabase-js,
// which already retries GET requests that fail this way (up to three more times, with a
// growing pause) — retrying here as well made one stuck request wait many deadlines in a
// row. Writes are never retried (they might have landed).
//
// Left alone on purpose:
//  - Storage calls (photo/design uploads and downloads): big files
//    legitimately take longer than the deadline.
//  - Token calls (/auth/v1/token — sign-in and session refresh): a refresh
//    swaps the refresh token for a new one and marks the old one used. If we
//    gave up on a refresh the server had in fact processed, the retry would
//    present that used token; past Supabase's short reuse window (~10s) that
//    reads as a stolen token and ends the whole session — a surprise logout,
//    which is far worse than a slow start.
export function createTimedFetch({
  timeoutMs = 10000,
  fetchImpl = (...args) => fetch(...args)
} = {}) {
  return async function timedFetch(input, init = {}) {
    const url = typeof input === "string" ? input : input?.url || String(input);
    // Sending an OTP code goes through the SMTP provider before Supabase
    // answers (see Login.jsx's OTP_SEND_TIMEOUT_MS comment) — its own
    // explicit withTimeout(..., 15000) around the call is deliberately
    // longer than this wrapper's default 10s, specifically to give that
    // extra grace period. Without this exclusion, the generic 10s abort
    // here fired first every time, silently capping the 15s intent back
    // down to 10.
    if (url.includes("/storage/v1/") || url.includes("/auth/v1/token") || url.includes("/auth/v1/otp")) {
      return fetchImpl(input, init);
    }

    const deadline = timeoutMs;

    async function attempt() {
      const controller = new AbortController();
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, deadline);

      // Still honor the caller's own cancellation (e.g. an aborted search).
      const callerSignal = init.signal;
      const onCallerAbort = () => controller.abort();
      if (callerSignal) {
        if (callerSignal.aborted) controller.abort();
        else callerSignal.addEventListener("abort", onCallerAbort, { once: true });
      }

      try {
        return await fetchImpl(input, { ...init, signal: controller.signal });
      } catch (err) {
        if (timedOut) {
          const timeoutError = new Error("Request timeout");
          timeoutError.name = "TimeoutError";
          timeoutError.timedOut = true;
          throw timeoutError;
        }
        throw err;
      } finally {
        clearTimeout(timer);
        callerSignal?.removeEventListener("abort", onCallerAbort);
      }
    }

    return attempt();
  };
}

// Auth calls (/auth/v1/token) are deliberately excluded from createTimedFetch
// above — retrying one that actually landed risks a "stolen token" surprise
// logout. But that means they have no deadline at all otherwise: a hung
// request (bad connection, a stuck token refresh) would leave a button
// reading "Sending code…"/"Verifying…" forever with no error and no way out.
// This races a promise against a timeout instead, so there's always a way
// forward — used directly around individual supabase.auth.* calls rather
// than at the fetch layer.
export function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
